// Polymarket API integration
// Docs: https://docs.polymarket.com/developers

/**
 * IMPORTANT NOTE ABOUT TRADING:
 * The trading functionality (CLOB client, order placement) requires server-side implementation
 * See: src/lib/polymarket-trading.ts for the server-side trading functions
 * This file focuses on read-only Polymarket data fetching
 */

// Note: db imports are done dynamically in server-only functions to avoid client-side errors
// Type duplicated here to avoid importing db.ts on client side
interface PolymarketUser {
  id?: string;
  address: string;
  username?: string;
  profile_image?: string;
  bio?: string;
  pnl?: number;
  volume?: number;
  markets_traded?: number;
  positions_value?: number;
  gain?: number;
  num_trades?: number;
  win_rate?: number;
  wins?: number;
  losses?: number;
  market_categories?: string;
  created_at?: string;
  updated_at?: string;
  last_fetched_at?: string;
}

const BACKEND_URL = process.env.BACKEND_URL || process.env.NEXT_PUBLIC_BACKEND_URL || "http://localhost:4000";
const BACKEND_API_KEY = process.env.WEB_API_KEY || process.env.API_KEY || "";

// Polymarket Subgraphs (hosted on Goldsky) - OFFICIAL ON-CHAIN DATA
const SUBGRAPH_PNL = "https://api.goldsky.com/api/public/project_cl6mb8i9h0003e201j6li0diw/subgraphs/pnl-subgraph/0.0.14/gn";
const SUBGRAPH_ORDERBOOK = "https://api.goldsky.com/api/public/project_cl6mb8i9h0003e201j6li0diw/subgraphs/orderbook-subgraph/0.0.1/gn";
const SUBGRAPH_ACTIVITY = "https://api.goldsky.com/api/public/project_cl6mb8i9h0003e201j6li0diw/subgraphs/activity-subgraph/0.0.4/gn";

// Polygon RPC and USDC contract address
const POLYGON_RPC = "https://polygon-rpc.com";
const USDC_CONTRACT_ADDRESS = "0x2791Bca1f2de4661ED88A30C99A7a9449Aa84174"; // USDC.e (bridged) on Polygon - used by Polymarket

async function fetchBackendJson<T>(path: string, revalidateSeconds?: number): Promise<T | null> {
  try {
    const response = await fetch(`${BACKEND_URL}${path}`, {
      headers: {
        Accept: "application/json",
        ...(BACKEND_API_KEY ? { "X-API-Key": BACKEND_API_KEY, "X-Forwarded-For": "127.0.0.1" } : {}),
      },
      ...(revalidateSeconds ? { next: { revalidate: revalidateSeconds } } : {}),
    });

    if (!response.ok) {
      return null;
    }

    return await response.json();
  } catch {
    return null;
  }
}

export interface PolymarketTrader {
  address: string;
  username?: string;
  avatar?: string;
  bio?: string;
  totalVolume: number;
  totalProfit: number;
  totalLoss: number;
  totalPnL?: number; // Total PnL (profit - loss)
  currentBalance?: number; // USDC balance in Polygon wallet (from blockchain)
  totalPositions: number;
  activePositions: number;
  wins: number;
  losses: number;
  winRate: number;
  positionsAboveEntry?: number; // Percentage of positions above entry price
  rank?: number;
  joinedDate?: string; // ISO date string (YYYY-MM-DD)
  marketCategories?: string[]; // Array of market categories like ["Politics", "Crypto", "Sports"]
  tradeCount?: number; // Number of trades executed (from backend num_trades)
  marketsCount?: number; // Number of markets traded
  // Stats from Polymarket scraping
  largestWin?: number; // Biggest win from user-stats
  profileViews?: number; // Profile views from user-stats
  polymarketJoinedAt?: string; // Polymarket join date from user-stats
  // Kalshi-specific fields
  followers?: number; // Kalshi follower count
  copierCount?: number; // active FrenFlow users copying this trader (social proof)
  joinedAt?: string | null; // Kalshi join date (ISO)
  // Predexon enrichment
  primaryStyle?: string; // WHALE, MARKET_MAKER, ACTIVE_TRADER, DEGEN, etc.
  // 2026-05-18: HFT bot flag (> 200 trades/h sustained over 24h).
  // Backend enrichTradersWithHftFlag sets these from a live count on
  // copytrading_leader_trades. Frontend renders a red HFT BOT badge.
  isHft?: boolean;
  tradesPerHour?: number;
  profit7d?: number;
  profit30d?: number;
  roi?: number | null;
  entryEdge?: number | null;
  avgBuyPrice?: number | null;
  avgSellPrice?: number | null;
  profitFactor?: number | null;
  avgTradeSize?: number | null;
  // Reference-window max drawdown % (all-time). Only set for curator "gems"
  // (Recommended tab) — Predexon/on-chain leaderboard has no MDD source.
  maxDrawdownPct?: number | null;
  // FrenFlow user enrichment (from backend LEFT JOIN on users table)
  isFrenFlowUser?: boolean;
  frenFlowUsername?: string | null;
  frenFlowName?: string | null;
  frenFlowAvatar?: string | null;
  // X (Twitter) handle linked on Polymarket — only set when the user
  // has connected their X account. Drives the "X only" filter and the
  // X icon link rendered on the trader card under that filter.
  xUsername?: string | null;
}

export interface Position {
  proxyWallet: string;
  asset: string;
  conditionId: string;
  size: number;
  avgPrice: number;
  currentValue: number;
  cashPnl: number;
  percentPnl: number;
  realizedPnl: number;
  title: string;
  slug: string;
  outcome: string;
  endDate: string;
  icon?: string;
  redeemable: boolean;
  mergeable: boolean;
  negativeRisk: boolean;
}

export interface Trade {
  side: "BUY" | "SELL";
  asset: string;
  size: number;
  price: number;
  timestamp: number;
  transactionHash: string;
  market: string;
  outcome: string;
}

export interface Activity {
  proxyWallet: string;
  timestamp: number;
  conditionId: string;
  type: "TRADE" | "SPLIT" | "MERGE" | "REDEEM" | "REWARD" | "CONVERSION";
  size: number;
  usdcSize: number;
  transactionHash: string;
  price?: number;
  asset?: string;
  side?: "BUY" | "SELL";
  outcomeIndex?: number;
  title?: string;
  slug?: string;
  icon?: string;
  outcome?: string;
  name?: string;
  pseudonym?: string;
  bio?: string;
  profileImage?: string;
  profileImageOptimized?: string;
}

export interface LeaderboardEntry {
  address: string;
  username?: string;
  profile_picture?: string;
  bio?: string;
  pnl: number;
  volume: number;
  positions: number;
  wins: number;
  losses: number;
  rank: number;
}

// Polymarket Subgraph response types
export interface SubgraphUserPosition {
  id: string;
  user: string;
  tokenId: string;
  amount: string; // BigInt as string
  avgPrice: string; // BigInt as string (scaled by 1e6)
  realizedPnl: string; // BigInt as string (scaled by 1e6)
  totalBought: string; // BigInt as string
}

export interface SubgraphOrderFilled {
  id: string;
  transactionHash: string;
  timestamp: string;
  orderHash: string;
  maker: string;
  taker: string;
  makerAssetId: string;
  takerAssetId: string;
  makerAmountFilled: string;
  takerAmountFilled: string;
  fee: string;
}

export interface SubgraphRedemption {
  id: string;
  timestamp: string;
  redeemer: string;
  condition: string;
  payout: string;
}

// ==========================================
// POLYMARKET SUBGRAPH QUERIES
// ==========================================

/**
 * Fetch user positions from PNL Subgraph
 * Returns positions with realized PnL, avg price, and amounts
 */
async function fetchSubgraphPositions(userAddress: string): Promise<SubgraphUserPosition[]> {
  try {
    const query = `
      query GetUserPositions($user: String!) {
        userPositions(
          where: { user: $user }
          first: 1000
          orderBy: realizedPnl
          orderDirection: desc
        ) {
          id
          user
          tokenId
          amount
          avgPrice
          realizedPnl
          totalBought
        }
      }
    `;

    const response = await fetch(SUBGRAPH_PNL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        query,
        variables: { user: userAddress.toLowerCase() }
      }),
      next: { revalidate: 60 },
    });

    if (!response.ok) {

      return [];
    }

    const data = await response.json();

    if (data.errors) {

      return [];
    }

    const positions = data.data?.userPositions || [];
    return positions;
  } catch (error) {

    return [];
  }
}

/**
 * Fetch order fills from Orderbook Subgraph
 * Returns all trades where user was maker or taker
 */
async function fetchSubgraphOrders(userAddress: string): Promise<SubgraphOrderFilled[]> {
  try {
    const userLower = userAddress.toLowerCase();

    // Query for orders where user is maker
    const makerQuery = `
      query GetMakerOrders($user: String!) {
        orderFilledEvents(
          where: { maker: $user }
          first: 1000
          orderBy: timestamp
          orderDirection: desc
        ) {
          id
          transactionHash
          timestamp
          orderHash
          maker
          taker
          makerAssetId
          takerAssetId
          makerAmountFilled
          takerAmountFilled
          fee
        }
      }
    `;

    // Query for orders where user is taker
    const takerQuery = `
      query GetTakerOrders($user: String!) {
        orderFilledEvents(
          where: { taker: $user }
          first: 1000
          orderBy: timestamp
          orderDirection: desc
        ) {
          id
          transactionHash
          timestamp
          orderHash
          maker
          taker
          makerAssetId
          takerAssetId
          makerAmountFilled
          takerAmountFilled
          fee
        }
      }
    `;

    const [makerResponse, takerResponse] = await Promise.all([
      fetch(SUBGRAPH_ORDERBOOK, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ query: makerQuery, variables: { user: userLower } }),
        next: { revalidate: 60 },
      }),
      fetch(SUBGRAPH_ORDERBOOK, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ query: takerQuery, variables: { user: userLower } }),
        next: { revalidate: 60 },
      })
    ]);

    const [makerData, takerData] = await Promise.all([
      makerResponse.json(),
      takerResponse.json()
    ]);

    const makerOrders = makerData.data?.orderFilledEvents || [];
    const takerOrders = takerData.data?.orderFilledEvents || [];

    // Combine and dedupe
    const allOrders = [...makerOrders, ...takerOrders];
    const uniqueOrders = allOrders.filter((order, index, self) =>
      index === self.findIndex(o => o.id === order.id)
    );

    return uniqueOrders;
  } catch (error) {

    return [];
  }
}

/**
 * Fetch redemptions from Activity Subgraph
 * Returns all redemptions (payouts from resolved markets)
 */
async function fetchSubgraphRedemptions(userAddress: string): Promise<SubgraphRedemption[]> {
  try {
    const query = `
      query GetRedemptions($user: String!) {
        redemptions(
          where: { redeemer: $user }
          first: 1000
          orderBy: timestamp
          orderDirection: desc
        ) {
          id
          timestamp
          redeemer
          condition
          payout
        }
      }
    `;

    const response = await fetch(SUBGRAPH_ACTIVITY, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        query,
        variables: { user: userAddress.toLowerCase() }
      }),
      next: { revalidate: 60 },
    });

    if (!response.ok) {

      return [];
    }

    const data = await response.json();

    if (data.errors) {

      return [];
    }

    const redemptions = data.data?.redemptions || [];
    return redemptions;
  } catch (error) {

    return [];
  }
}

/**
 * Calculate stats from Subgraph data
 * This uses on-chain data which is more accurate than API data
 */
async function calculateStatsFromSubgraph(userAddress: string): Promise<{
  realizedPnl: number;
  totalBought: number;
  totalVolume: number;
  numTrades: number;
  numPositions: number;
  redemptionPayout: number;
}> {
  try {
    const [positions, orders, redemptions] = await Promise.all([
      fetchSubgraphPositions(userAddress),
      fetchSubgraphOrders(userAddress),
      fetchSubgraphRedemptions(userAddress),
    ]);

    // Calculate realized PnL from positions (scaled by 1e6)
    const realizedPnl = positions.reduce((sum, p) => {
      return sum + (Number(p.realizedPnl) / 1e6);
    }, 0);

    // Calculate total bought from positions (scaled by 1e6)
    const totalBought = positions.reduce((sum, p) => {
      return sum + (Number(p.totalBought) / 1e6);
    }, 0);

    // Calculate volume from orders (sum of maker and taker amounts)
    // USDC amounts are scaled by 1e6
    const totalVolume = orders.reduce((sum, o) => {
      const makerAmount = Number(o.makerAmountFilled) / 1e6;
      const takerAmount = Number(o.takerAmountFilled) / 1e6;
      // Volume is the USDC side of the trade
      // If makerAssetId is "0" (USDC), use makerAmount, else takerAmount
      const usdcAmount = o.makerAssetId === "0" ? makerAmount : takerAmount;
      return sum + usdcAmount;
    }, 0);

    // Calculate redemption payouts
    const redemptionPayout = redemptions.reduce((sum, r) => {
      return sum + (Number(r.payout) / 1e6);
    }, 0);

    return {
      realizedPnl,
      totalBought,
      totalVolume,
      numTrades: orders.length,
      numPositions: positions.length,
      redemptionPayout,
    };
  } catch (error) {

    return {
      realizedPnl: 0,
      totalBought: 0,
      totalVolume: 0,
      numTrades: 0,
      numPositions: 0,
      redemptionPayout: 0,
    };
  }
}

// Generate mock leaderboard data
// TODO: Replace with real API when Polymarket provides leaderboard endpoint
function generateMockLeaderboard(limit: number, offset: number): PolymarketTrader[] {
  const mockUsernames = [
    "CryptoWhale", "PoliticsGuru", "MarketMaker", "TradeKing", "BetMaster",
    "Prophet99", "SmartMoney", "OddsAnalyst", "MarketSage", "TrendTrader",
    "VolumeBeast", "ProfitHunter", "RiskTaker", "ValueSeeker", "SharpBettor",
    "DataDriven", "MarketWatch", "TradePro", "BetWise", "OddsKing",
  ];

  const traders: PolymarketTrader[] = [];

  for (let i = 0; i < limit; i++) {
    const index = offset + i;
    const rank = index + 1;

    // Generate realistic values that decrease with rank
    const baseVolume = 500000 / Math.pow(rank, 0.7);
    const winRate = Math.max(50, 90 - (rank * 0.5) + (Math.random() * 10 - 5));
    const wins = Math.floor(50 + (100 - rank) * 2 + Math.random() * 50);
    const losses = Math.floor(wins / (winRate / (100 - winRate)));

    traders.push({
      address: `0x${Math.random().toString(16).slice(2, 10).padEnd(40, '0')}`,
      username: index < mockUsernames.length ? mockUsernames[index] : `Trader${rank}`,
      avatar: "/avatar.jpg",
      bio: "Polymarket trader focusing on politics and crypto markets",
      totalVolume: Math.floor(baseVolume),
      totalProfit: Math.floor(baseVolume * (winRate / 100) * 0.3),
      totalLoss: Math.floor(baseVolume * ((100 - winRate) / 100) * 0.3),
      totalPositions: wins + losses,
      activePositions: Math.floor(Math.random() * 10) + 3,
      wins,
      losses,
      winRate: Math.round(winRate),
      rank,
    });
  }

  return traders;
}

/**
 * Fetch leaderboard data from Polymarket
 * NOTE: Currently using mock data as Polymarket doesn't have a public leaderboard API
 * TODO: Integrate with real API when available
 */
export async function fetchLeaderboard(
  limit: number = 100,
  offset: number = 0
): Promise<PolymarketTrader[]> {
  try {
    // Return mock data for now
    // In production, this would call a real API endpoint
    return generateMockLeaderboard(limit, offset);
  } catch (error) {

    return [];
  }
}

/**
 * Fetch trader data by wallet address
 * NOTE: Currently searches in mock leaderboard data
 */
export async function fetchTraderByAddress(
  address: string
): Promise<PolymarketTrader | null> {
  try {
    // Search in leaderboard data
    const leaderboard = await fetchLeaderboard(500);
    const trader = leaderboard.find(
      (t) => t.address.toLowerCase() === address.toLowerCase()
    );

    return trader || null;
  } catch (error) {

    return null;
  }
}

/**
 * Search traders by username or wallet address
 * If query looks like a wallet address, fetch real data from Polymarket API
 * If it's a username, search in recent trades and fetch real data
 */
export async function searchTraders(
  query: string
): Promise<PolymarketTrader[]> {
  try {
    // Decode URL encoding and clean the query
    const decodedQuery = decodeURIComponent(query).trim();
    // Remove @ if present at the start
    let cleanQuery = decodedQuery.startsWith('@') ? decodedQuery.slice(1) : decodedQuery;

    // Extract wallet address from Polymarket profile URL
    // Supports: https://polymarket.com/profile/0x... or polymarket.com/profile/0x...
    const polymarketUrlMatch = cleanQuery.match(/(?:https?:\/\/)?(?:www\.)?polymarket\.com\/profile\/(0x[a-fA-F0-9]{40})/i);
    if (polymarketUrlMatch) {
      cleanQuery = polymarketUrlMatch[1];

    }

    const searchLower = cleanQuery.toLowerCase();

    const backendSearch = await fetchBackendJson<{ traders?: PolymarketTrader[] }>(
      `/api/portfolio/traders/search?q=${encodeURIComponent(cleanQuery)}&limit=10`,
      15
    );
    if (Array.isArray(backendSearch?.traders) && backendSearch.traders.length > 0) {
      return backendSearch.traders;
    }

    // Check if query is a wallet address (starts with 0x and has 40+ hex chars)
    const isWalletAddress = /^0x[a-fA-F0-9]{40}$/i.test(cleanQuery);

    if (isWalletAddress) {
      const realTrader = await fetchRealTraderByAddress(cleanQuery);
      return realTrader ? [realTrader] : [];
    }

    // Fallback to mock leaderboard by username/address
    const leaderboard = await fetchLeaderboard(500);

    return leaderboard.filter(
      (trader) =>
        trader.username?.toLowerCase().includes(searchLower) ||
        trader.address.toLowerCase().includes(searchLower)
    );
  } catch (error) {

    return [];
  }
}

/**
 * Resolve a Polymarket username to a wallet address by fetching their profile page
 * This is much faster than searching through recent trades
 */
async function resolveUsernameToAddress(username: string): Promise<string | null> {
  try {
    // Fetch the Polymarket profile page directly
    const url = `https://polymarket.com/profile/@${encodeURIComponent(username)}`;
    const response = await fetch(url, {
      headers: {
        'User-Agent': 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/120.0.0.0 Safari/537.36',
        'Accept': 'text/html,application/xhtml+xml,application/xml;q=0.9,image/webp,*/*;q=0.8',
      },
      next: { revalidate: 300 }, // Cache for 5 minutes
    });

    if (!response.ok) {
      return null;
    }

    const html = await response.text();

    // Extract __NEXT_DATA__ JSON which contains the user's address
    const nextDataMatch = html.match(/<script id="__NEXT_DATA__"[^>]*>([^<]+)<\/script>/);
    if (!nextDataMatch) {
      return null;
    }

    const nextData = JSON.parse(nextDataMatch[1]);

    // The address is in the page props - try multiple paths
    const address =
      nextData?.props?.pageProps?.dehydratedState?.queries?.[0]?.state?.data?.proxyWallet ||
      nextData?.props?.pageProps?.user?.proxyWallet ||
      nextData?.props?.pageProps?.address;

    if (address && /^0x[a-fA-F0-9]{40}$/.test(address)) {
      return address;
    }

    return null;
  } catch {
    return null;
  }
}

/**
 * Find a trader by username
 * Uses direct Polymarket profile lookup for fast resolution
 */
export async function findTraderByUsername(
  username: string
): Promise<PolymarketTrader | null> {
  try {
    // Decode URL encoding and clean the username
    const decodedUsername = decodeURIComponent(username).trim();
    // Remove @ if present at the start
    const cleanUsername = decodedUsername.startsWith('@') ? decodedUsername.slice(1) : decodedUsername;

    const backendProfile = await fetchBackendJson<{ trader?: PolymarketTrader }>(
      `/api/portfolio/traders/${encodeURIComponent(cleanUsername)}`,
      30
    );
    if (backendProfile?.trader) {
      return backendProfile.trader;
    }

    // First, check if user exists in database by username
    const { getCachedUserByUsername } = await import("@/lib/db");
    const cachedUser = await getCachedUserByUsername(cleanUsername);

    if (cachedUser) {
      // Use fetchRealTraderByAddress which handles the caching logic
      return await fetchRealTraderByAddress(cachedUser.address);
    }

    // Resolve username to address via Polymarket profile page (fast!)
    const address = await resolveUsernameToAddress(cleanUsername);

    if (!address) {
      return null;
    }

    // Fetch full trader data using the resolved address
    return await fetchRealTraderByAddress(address);
  } catch {
    return null;
  }
}

/**
 * Format volume for display
 */
export function formatVolume(volume: number | undefined | null): string {
  const numVolume = Number(volume) || 0;
  if (numVolume >= 1000000) {
    return `$${(numVolume / 1000000).toFixed(1)}M`;
  } else if (numVolume >= 1000) {
    return `$${(numVolume / 1000).toFixed(1)}K`;
  }
  return `$${numVolume.toFixed(0)}`;
}

/**
 * Calculate copiers count (mock for now - would need separate API)
 */
export function calculateCopiers(trader: PolymarketTrader): number {
  // Mock calculation based on volume and rank
  const baseScore = (trader.totalVolume || 0) / 1000;
  const rankBonus = trader.rank ? Math.max(0, 100 - trader.rank) * 10 : 0;
  return Math.floor(baseScore + rankBonus);
}

/**
 * Fetch USDC balance from Polygon blockchain
 * Uses the ERC20 balanceOf function via RPC
 */
export async function fetchUSDCBalance(walletAddress: string): Promise<number> {
  try {
    // ERC20 balanceOf function signature: balanceOf(address)
    // Function selector: 0x70a08231
    const data = `0x70a08231000000000000000000000000${walletAddress.slice(2).toLowerCase()}`;

    const response = await fetch(POLYGON_RPC, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        jsonrpc: "2.0",
        method: "eth_call",
        params: [
          {
            to: USDC_CONTRACT_ADDRESS,
            data: data,
          },
          "latest",
        ],
        id: 1,
      }),
    });

    const result = await response.json();

    if (result.error) {

      return 0;
    }

    // Parse hex result and convert from 6 decimals (USDC has 6 decimals)
    const balanceHex = result.result;
    const balanceWei = BigInt(balanceHex);
    const balance = Number(balanceWei) / 1_000_000; // USDC has 6 decimals

    return balance;
  } catch (error) {
    return 0;
  }
}


/**
 * Fetch user positions from Polymarket Data API
 * Automatically paginates to get ALL positions
 */
export async function fetchUserPositions(
  userAddress: string,
  limit: number = 500
): Promise<Position[]> {
  try {
    const cappedLimit = Math.min(limit, 100);
    const allPositions: Position[] = [];
    let offset = 0;
    let hasMore = true;

    while (hasMore && offset < 1000) {
      const backendResponse = await fetchBackendJson<{ positions?: Array<any> }>(
        `/api/portfolio/traders/${encodeURIComponent(userAddress)}/positions?limit=${cappedLimit}&offset=${offset}`,
        60
      );

      const rawPositions = Array.isArray(backendResponse?.positions) ? backendResponse.positions : [];
      if (rawPositions.length === 0) {
        break;
      }

      allPositions.push(
        ...rawPositions.map((position: any) => ({
          proxyWallet: position.proxyWallet || userAddress,
          asset: position.asset || position.slug || "",
          conditionId: position.conditionId || "",
          size: Number(position.size) || 0,
          avgPrice: Number(position.avgPrice) || 0,
          currentValue: Number(position.currentValue) || 0,
          cashPnl: Number(position.pnl) || 0,
          percentPnl: Number(position.pnlPercent) || 0,
          realizedPnl: Number(position.realizedPnl) || 0,
          title: position.title || "",
          slug: position.slug || "",
          outcome: position.outcome || "Yes",
          endDate: position.endDate || "",
          icon: position.icon || undefined,
          redeemable: Boolean(position.redeemable),
          mergeable: false,
          negativeRisk: false,
        }))
      );

      offset += rawPositions.length;
      hasMore = rawPositions.length >= cappedLimit;

      if (rawPositions.length < cappedLimit) {
        hasMore = false;
      }
    }

    return allPositions;
  } catch (error) {
    return [];
  }
}

/**
 * Fetch user trades from Polymarket Data API
 */
export async function fetchUserTrades(
  userAddress: string,
  limit: number = 100
): Promise<Trade[]> {
  try {
    const backendTrades = await fetchBackendJson<{ trades?: Array<any> }>(
      `/api/portfolio/traders/${encodeURIComponent(userAddress)}/trades?limit=${Math.min(limit, 500)}`,
      60
    );
    if (!Array.isArray(backendTrades?.trades)) {
      return [];
    }

    return backendTrades.trades.map((trade: any) => ({
      side: trade.side === "SELL" ? "SELL" : "BUY",
      asset: trade.asset || trade.slug || trade.market || "",
      size: Number(trade.size) || 0,
      price: Number(trade.price) || 0,
      timestamp: typeof trade.timestamp === "number"
        ? (trade.timestamp > 1e12 ? Math.floor(trade.timestamp / 1000) : trade.timestamp)
        : (trade.date ? Math.floor(new Date(trade.date).getTime() / 1000) : 0),
      transactionHash: trade.transactionHash || trade.id || "",
      market: trade.market || trade.title || trade.question || "",
      outcome: trade.outcome || trade.position || "Unknown",
    }));
  } catch (error) {

    return [];
  }
}

/**
 * Fetch user activity from Polymarket Data API (includes trades, redeems, etc.)
 * This is more comprehensive than the /trades endpoint
 * Automatically paginates to get ALL activity
 */
export async function fetchUserActivity(
  userAddress: string,
  limit: number = 100,
  type?: "TRADE" | "REDEEM" | "SPLIT" | "MERGE" | "REWARD" | "CONVERSION"
): Promise<Activity[]> {
  try {
    const params = new URLSearchParams({
      limit: String(Math.min(limit, 200)),
    });
    if (type) {
      params.set("type", type);
    }

    const backendActivity = await fetchBackendJson<{ activities?: Array<any> }>(
      `/api/portfolio/traders/${encodeURIComponent(userAddress)}/activity?${params.toString()}`,
      30
    );
    if (!Array.isArray(backendActivity?.activities)) {
      return [];
    }

    return backendActivity.activities.map((activity: any) => ({
      proxyWallet: activity.proxyWallet || userAddress,
      timestamp: Number(activity.timestamp) || 0,
      conditionId: activity.conditionId || "",
      type: activity.type || "TRADE",
      size: Number(activity.size) || 0,
      usdcSize: Number(activity.usdcSize) || 0,
      transactionHash: activity.transactionHash || "",
      price: activity.price != null ? Number(activity.price) || 0 : undefined,
      asset: activity.asset || undefined,
      side: activity.side || undefined,
      outcomeIndex: activity.outcomeIndex != null ? Number(activity.outcomeIndex) || 0 : undefined,
      title: activity.title || undefined,
      slug: activity.slug || undefined,
      icon: activity.icon || undefined,
      outcome: activity.outcome || undefined,
      name: activity.name || undefined,
      pseudonym: activity.pseudonym || undefined,
      bio: activity.bio || undefined,
      profileImage: activity.profileImage || undefined,
      profileImageOptimized: activity.profileImageOptimized || undefined,
    }));
  } catch (error) {
    return [];
  }
}

/**
 * Fetch user portfolio value from Polymarket Data API
 */
export async function fetchUserValue(
  userAddress: string
): Promise<number> {
  try {
    const backendValue = await fetchBackendJson<{ value?: number }>(
      `/api/portfolio/traders/${encodeURIComponent(userAddress)}/value`,
      60
    );
    return Number(backendValue?.value) || 0;
  } catch (error) {

    return 0;
  }
}

/**
 * Fetch the total number of markets traded (predictions count)
 * Uses the /traded endpoint which returns the official count matching Polymarket
 */
export async function fetchTradedCount(
  userAddress: string
): Promise<number> {
  try {
    const backendTraded = await fetchBackendJson<{ traded?: number }>(
      `/api/portfolio/traders/${encodeURIComponent(userAddress)}/traded`,
      60
    );
    return Number(backendTraded?.traded) || 0;
  } catch (error) {

    return 0;
  }
}

export interface RecentTrade {
  id: string;
  market: string;
  position: string;
  outcome: string;
  profit: string;
  date: string;
  type: "BUY" | "SELL";
  usdcSize: number;
  icon?: string;
  slug?: string;
}

/**
 * Calculate trader stats from real API data using the comprehensive /activity endpoint
 * Uses COMPLETE historical data with pagination
 *
 * FORMULAS (verified against Polymarket official):
 * - PnL = (Total Sold + Total Redeemed + Current Value) - Total Bought
 * - Predictions = Count of unique condition IDs from all activity
 * - Current Balance = USDC balance in Polygon wallet (from blockchain)
 */
export async function calculateTraderStats(
  userAddress: string
): Promise<Partial<PolymarketTrader> | null> {
  try {
    // Fetch ALL activity, positions, portfolio value, and USDC balance (with pagination)
    const [allActivity, positions, currentValue, usdcBalance] = await Promise.all([
      fetchUserActivity(userAddress),
      fetchUserPositions(userAddress),
      fetchUserValue(userAddress), // For PnL calculation
      fetchUSDCBalance(userAddress), // For Current Balance display
    ]);

    // Filter by activity type
    const trades = allActivity.filter((a) => a.type === "TRADE");
    const redeems = allActivity.filter((a) => a.type === "REDEEM");

    // Calculate volume from ALL activity (trades + redeems)
    // Volume is the sum of absolute values of all USDC movements
    const totalVolume = allActivity.reduce((sum, a) => sum + Math.abs(a.usdcSize || 0), 0);

    // Calculate money in (buys) and money out (sells + redeems)
    const totalBought = trades
      .filter((t) => t.side === "BUY")
      .reduce((sum, t) => sum + t.usdcSize, 0);

    const totalSold = trades
      .filter((t) => t.side === "SELL")
      .reduce((sum, t) => sum + t.usdcSize, 0);

    const totalRedeemed = redeems.reduce((sum, r) => sum + r.usdcSize, 0);

    // Calculate PnL: (Sold + Redeemed + CurrentValue) - Bought
    // This matches Polymarket's official calculation (verified: $81.84 vs $82.19)
    const totalPnL = totalSold + totalRedeemed + currentValue - totalBought;
    const totalProfit = totalPnL > 0 ? totalPnL : 0;
    const totalLoss = totalPnL < 0 ? Math.abs(totalPnL) : 0;

    // Calculate win rate from ALL positions (including active ones with positive PnL)
    const profitablePositions = positions.filter((p) => p.cashPnl > 0).length;
    const losingPositions = positions.filter((p) => p.cashPnl <= 0).length;
    const wins = profitablePositions;
    const losses = losingPositions;
    const winRate = positions.length > 0 ? Math.round((wins / positions.length) * 100) : 0;

    // Count active positions
    const activePositions = positions.filter((p) => !p.redeemable).length;

    // Count unique predictions (unique condition IDs from all activity)
    // This matches Polymarket's "Predictions" count (verified: 236 = 236)
    const uniqueConditionIds = new Set<string>();
    allActivity.forEach((a) => {
      if (a.conditionId) {
        uniqueConditionIds.add(a.conditionId);
      }
    });
    const totalPredictions = uniqueConditionIds.size;

    // Extract username and profile data if available from activity
    const activityWithProfile = allActivity.find((a) => a.name);
    const username = activityWithProfile?.name;
    const profileImage = activityWithProfile?.profileImageOptimized || activityWithProfile?.profileImage;
    const bio = activityWithProfile?.bio;

    // Get first trade timestamp (joined date approximation)
    const tradesOnly = allActivity.filter((a) => a.type === "TRADE");
    const sortedTrades = tradesOnly.sort((a, b) => a.timestamp - b.timestamp);
    const firstTradeTimestamp = sortedTrades[0]?.timestamp;
    const joinedDate = firstTradeTimestamp
      ? new Date(firstTradeTimestamp * 1000).toISOString().split('T')[0]
      : undefined;

    // Calculate percentage of positions above entry (profitable)
    const positionsAboveEntry = positions.length > 0
      ? Math.round((profitablePositions / positions.length) * 100)
      : 0;

    return {
      address: userAddress,
      username,
      avatar: profileImage,
      bio,
      totalVolume,
      totalProfit,
      totalLoss,
      totalPnL, // Total PnL
      currentBalance: usdcBalance, // USDC balance in Polygon wallet (from blockchain)
      totalPositions: totalPredictions, // Use unique predictions count
      activePositions,
      wins,
      losses,
      winRate,
      positionsAboveEntry, // Percentage of positions with profit
      joinedDate,
    };
  } catch (error) {

    return null;
  }
}

/**
 * Get recent trades for a user from their activity
 */
export async function getRecentTrades(
  userAddress: string,
  limit: number = 10
): Promise<RecentTrade[]> {
  try {
    const backendTrades = await fetchBackendJson<{ trades?: Array<any> }>(
      `/api/portfolio/traders/${encodeURIComponent(userAddress)}/recent-trades?limit=${Math.min(limit, 20)}`,
      60
    );
    if (Array.isArray(backendTrades?.trades)) {
      return backendTrades.trades.map((trade: any) => ({
        id: trade.id || `${trade.date}-${trade.market}`,
        market: trade.market || "Unknown Market",
        position: trade.position || "Unknown",
        outcome: trade.type === "BUY" ? "opened" : "closed",
        profit: trade.type === "BUY"
          ? `-$${Number(trade.value || 0).toFixed(2)}`
          : `+$${Number(trade.value || 0).toFixed(2)}`,
        date: trade.date || new Date().toISOString(),
        type: trade.type || "BUY",
        usdcSize: Number(trade.value || 0),
        icon: trade.icon || "",
        slug: trade.slug || null,
      }));
    }

    const activity = await fetchUserActivity(userAddress, 100, "TRADE");

    // Sort by timestamp descending (most recent first)
    const sortedTrades = activity
      .filter((a) => a.type === "TRADE")
      .sort((a, b) => b.timestamp - a.timestamp)
      .slice(0, limit);

    return sortedTrades.map((trade) => ({
      id: trade.transactionHash,
      market: trade.title || trade.slug || "Unknown Market",
      position: trade.outcome || "Unknown",
      outcome: trade.side === "BUY" ? "opened" : "closed",
      profit: trade.side === "BUY" ? `-$${trade.usdcSize.toFixed(2)}` : `+$${trade.usdcSize.toFixed(2)}`,
      date: new Date(trade.timestamp * 1000).toISOString(),
      type: trade.side || "BUY",
      usdcSize: trade.usdcSize,
      icon: trade.icon || "", // Market icon/image
      slug: trade.slug, // Market slug for quick buy link
    }));
  } catch (error) {

    return [];
  }
}


/**
 * Calculate trader stats using Polymarket Subgraphs + Data API
 *
 * Data sources:
 * - PNL Subgraph: realizedPnl, positions count (ON-CHAIN DATA)
 * - Orderbook Subgraph: volume, trades count (ON-CHAIN DATA)
 * - Activity Subgraph: redemptions (ON-CHAIN DATA)
 * - Data API: activity (for profile info), positions (for current state)
 * - Blockchain RPC: USDC balance
 */
export async function calculateTraderStatsFromSubgraph(
  userAddress: string
): Promise<Partial<PolymarketTrader> | null> {
  try {
    // Fetch from multiple sources in parallel
    const [subgraphStats, polymarketPositions, polymarketActivity, currentValue, usdcBalance, tradedCount] = await Promise.all([
      calculateStatsFromSubgraph(userAddress), // Subgraph for PnL, volume, trades
      fetchUserPositions(userAddress), // Data API for current positions
      fetchUserActivity(userAddress, 100), // Data API for profile info (limited)
      fetchUserValue(userAddress), // Data API for portfolio value
      fetchUSDCBalance(userAddress), // Blockchain for USDC balance
      fetchTradedCount(userAddress), // Data API for official predictions count
    ]);

    // Use subgraph data as primary source (on-chain = accurate)
    // If subgraph returns 0 for volume/trades, fallback to Data API
    const totalVolume = subgraphStats.totalVolume > 0
      ? subgraphStats.totalVolume
      : polymarketActivity.reduce((sum, a) => sum + Math.abs(a.usdcSize || 0), 0);

    // Total predictions from /traded endpoint (matches Polymarket exactly)
    const totalPredictions = tradedCount > 0
      ? tradedCount
      : new Set(polymarketActivity.map((a) => a.conditionId).filter(Boolean)).size;

    // Calculate PnL: realizedPnl + unrealized (currentValue)
    // The subgraph realizedPnl is from closed positions
    // currentValue is the value of open positions
    // Total PnL = realized + unrealized - what's still invested
    const realizedPnl = subgraphStats.realizedPnl;
    const unrealizedPnl = polymarketPositions.reduce((sum, p) => sum + (p.cashPnl || 0), 0);
    const totalPnL = realizedPnl + unrealizedPnl;
    const totalProfit = totalPnL > 0 ? totalPnL : 0;
    const totalLoss = totalPnL < 0 ? Math.abs(totalPnL) : 0;

    // Calculate win rate from current Polymarket positions
    const profitablePositions = polymarketPositions.filter((p) => p.cashPnl > 0).length;
    const losingPositions = polymarketPositions.filter((p) => p.cashPnl <= 0).length;
    const wins = profitablePositions;
    const losses = losingPositions;
    const winRate = polymarketPositions.length > 0 ? Math.round((wins / polymarketPositions.length) * 100) : 0;

    // Positions above entry (profitable positions)
    const positionsAboveEntry = polymarketPositions.length > 0
      ? Math.round((profitablePositions / polymarketPositions.length) * 100)
      : 0;

    // Active positions
    const activePositions = polymarketPositions.filter((p) => !p.redeemable).length;

    // Get username from Polymarket activity
    const activityWithProfile = polymarketActivity.find((a) => a.name);
    const username = activityWithProfile?.name;
    const profileImage = activityWithProfile?.profileImageOptimized || activityWithProfile?.profileImage;
    const bio = activityWithProfile?.bio;

    // Get joined date from first trade in activity
    const sortedTrades = polymarketActivity.filter((a) => a.type === "TRADE").sort((a, b) => a.timestamp - b.timestamp);
    const firstTrade = sortedTrades[0];
    const joinedDate = firstTrade ? new Date(firstTrade.timestamp * 1000).toISOString().split('T')[0] : undefined;

    return {
      address: userAddress,
      username,
      avatar: profileImage,
      bio,
      totalVolume,
      totalProfit,
      totalLoss,
      totalPnL,
      currentBalance: usdcBalance,
      totalPositions: totalPredictions,
      activePositions,
      wins,
      losses,
      winRate,
      positionsAboveEntry,
      joinedDate,
    };
  } catch (error) {

    // Full fallback to Polymarket Data API
    return await calculateTraderStats(userAddress);
  }
}

// Keep the old function name for backwards compatibility
export const calculateTraderStatsFromHashDive = calculateTraderStatsFromSubgraph;

// ==========================================
// PNL HISTORY FOR CHART
// ==========================================

interface PnLHistoryPoint {
  timestamp: number;
  pnl: number;
  type: string;
}

interface PnLHistoryResponse {
  period: string;
  data: PnLHistoryPoint[];
  startPnL: number;
  currentPnL: number;
  change: number;
  changePercent: number;
}

/**
 * Calculate Total PnL using the correct formula:
 * Total PnL = sum(positions.cashPnl) + sum(subgraph.realizedPnl)
 *
 * This matches Polymarket's calculation exactly.
 */
async function calculateTotalPnL(userAddress: string): Promise<number> {
  try {
    // Fetch positions and subgraph in PARALLEL for speed
    const [positions, subgraphRes] = await Promise.all([
      fetchUserPositions(userAddress, 100),
      fetch(SUBGRAPH_PNL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        // SECURITY: Use GraphQL variables to prevent injection attacks
        body: JSON.stringify({
          query: `query GetUserPositions($user: String!) {
            userPositions(where: { user: $user }, first: 1000) {
              realizedPnl
            }
          }`,
          variables: { user: userAddress.toLowerCase() },
        }),
      }),
    ]);

    const totalCashPnl = positions.reduce((sum: number, p: { cashPnl?: number }) =>
      sum + (p.cashPnl || 0), 0);

    let totalSubgraphPnl = 0;
    if (subgraphRes.ok) {
      const data = await subgraphRes.json();
      if (data.data?.userPositions) {
        totalSubgraphPnl = data.data.userPositions.reduce(
          (sum: number, p: { realizedPnl: string }) =>
            sum + (parseInt(p.realizedPnl) / 1e6), 0
        );
      }
    }

    // Total = cashPnl + subgraph realizedPnl
    return totalCashPnl + totalSubgraphPnl;
  } catch (error) {

    return 0;
  }
}

/**
 * Fetch activity data for building historical PnL chart
 * Uses the /activity endpoint which has timestamps for all trades
 */
async function fetchActivityHistory(
  userAddress: string,
  limit: number = 1000
): Promise<Array<{ timestamp: number; side: string; usdcSize: number; price: number; size: number }>> {
  try {
    const activities = await fetchUserActivity(userAddress, limit);
    return activities
      .filter((a: { type: string }) => a.type === "TRADE" || a.type === "REDEEM")
      .map((a: { timestamp: number; side?: string; usdcSize: number; price?: number; size: number; type: string }) => ({
        timestamp: a.timestamp,
        side: a.type === "REDEEM" ? "REDEEM" : (a.side || "BUY"),
        usdcSize: a.usdcSize || 0,
        price: a.price || 0,
        size: a.size || 0,
      }));
  } catch (error) {

    return [];
  }
}

/**
 * Fetch PnL history for chart display
 *
 * Uses two data sources:
 * 1. Total PnL = positions.cashPnl + subgraph.realizedPnl (matches Polymarket exactly)
 * 2. Activity history for building the timeline chart
 *
 * Note: The chart shows cash flow progression as an approximation of PnL history.
 * The total PnL value is always accurate (matches Polymarket).
 */
export async function fetchPnLHistory(
  userAddress: string,
  period: "1d" | "7d" | "30d" | "90d" | "all" = "30d"
): Promise<PnLHistoryResponse | null> {
  try {
    const backendPeriodMap: Record<string, string> = {
      "1d": "1d",
      "7d": "7d",
      "30d": "30d",
      "90d": "all",
      "all": "all",
    };
    const backendHistory = await fetchBackendJson<PnLHistoryResponse>(
      `/api/portfolio/traders/${encodeURIComponent(userAddress)}/pnl-history?period=${encodeURIComponent(backendPeriodMap[period] || "30d")}`,
      60
    );
    if (backendHistory?.data) {
      return backendHistory;
    }

    // Determine activity limit based on period
    // Keep limits low for faster response - we only need enough for the chart
    const activityLimits: Record<string, number> = {
      "1d": 50,
      "7d": 100,
      "30d": 200,
      "90d": 300,
      "all": 500,
    };
    const activityLimit = activityLimits[period] || 500;

    // Fetch BOTH in PARALLEL for speed (saves ~1s)
    const [currentTotalPnL, activities] = await Promise.all([
      calculateTotalPnL(userAddress),
      fetchActivityHistory(userAddress, activityLimit),
    ]);
    if (activities.length === 0) {
      return {
        period,
        data: [{ timestamp: Math.floor(Date.now() / 1000), pnl: currentTotalPnL, type: "CURRENT" }],
        startPnL: 0,
        currentPnL: currentTotalPnL,
        change: currentTotalPnL,
        changePercent: 0,
      };
    }

    // Sort activities by timestamp ascending (oldest first)
    activities.sort((a, b) => a.timestamp - b.timestamp);

    // Calculate period cutoff
    const now = Math.floor(Date.now() / 1000);
    let cutoffTimestamp = 0;

    switch (period) {
      case "1d":
        cutoffTimestamp = now - 1 * 24 * 60 * 60;
        break;
      case "7d":
        cutoffTimestamp = now - 7 * 24 * 60 * 60;
        break;
      case "30d":
        cutoffTimestamp = now - 30 * 24 * 60 * 60;
        break;
      case "90d":
        cutoffTimestamp = now - 90 * 24 * 60 * 60;
        break;
      case "all":
        cutoffTimestamp = 0;
        break;
    }

    // Build cumulative cash flow for chart
    // BUY = negative (money out), SELL/REDEEM = positive (money in)
    const pnlData: PnLHistoryPoint[] = [];
    let cumulativeFlow = 0;
    let flowDuringPeriod = 0;  // Cash flow only during the selected period

    // Build data points and track cash flow during period
    for (const activity of activities) {
      if (activity.side === "BUY") {
        cumulativeFlow -= activity.usdcSize;
        if (activity.timestamp >= cutoffTimestamp) {
          flowDuringPeriod -= activity.usdcSize;
        }
      } else if (activity.side === "SELL" || activity.side === "REDEEM") {
        cumulativeFlow += activity.usdcSize;
        if (activity.timestamp >= cutoffTimestamp) {
          flowDuringPeriod += activity.usdcSize;
        }
      }

      if (activity.timestamp >= cutoffTimestamp) {
        pnlData.push({
          timestamp: activity.timestamp,
          pnl: cumulativeFlow,
          type: "ACTIVITY",
        });
      }
    }

    // For "all" period: start from 0, change = currentTotalPnL
    // For other periods: estimate start PnL based on cash flow during period
    let startPnL: number;
    let change: number;

    if (period === "all") {
      startPnL = 0;
      change = currentTotalPnL;
    } else {
      // Estimate: startPnL = currentPnL - (cash flow during period)
      // This assumes cash flow roughly equals PnL change (not perfect but reasonable)
      startPnL = currentTotalPnL - flowDuringPeriod;
      change = flowDuringPeriod;
    }

    if (pnlData.length === 0) {
      // No activity in period, show current state with no change
      return {
        period,
        data: [{ timestamp: now, pnl: currentTotalPnL, type: "CURRENT" }],
        startPnL: currentTotalPnL,
        currentPnL: currentTotalPnL,
        change: 0,
        changePercent: 0,
      };
    }

    // Scale chart data to end at currentTotalPnL
    const lastCumulativeFlow = pnlData[pnlData.length - 1].pnl;
    if (lastCumulativeFlow !== 0) {
      const scaleFactor = currentTotalPnL / lastCumulativeFlow;
      for (const point of pnlData) {
        point.pnl *= scaleFactor;
      }
    }

    // Aggregate data points by day to reduce noise
    const dailyData = aggregateByDay(pnlData);

    // Ensure the last point is exactly current total PnL
    if (dailyData.length > 0) {
      dailyData[dailyData.length - 1].pnl = currentTotalPnL;
    }

    const changePercent = startPnL !== 0 ? (change / Math.abs(startPnL)) * 100 : 0;

    return {
      period,
      data: dailyData,
      startPnL,
      currentPnL: currentTotalPnL,
      change,
      changePercent,
    };
  } catch (error) {

    return null;
  }
}

/**
 * Aggregate PnL data points by day (taking the last value of each day)
 */
function aggregateByDay(data: PnLHistoryPoint[]): PnLHistoryPoint[] {
  if (data.length === 0) return [];

  const dayMap = new Map<string, PnLHistoryPoint>();

  for (const point of data) {
    const date = new Date(point.timestamp * 1000);
    const dayKey = `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;

    // Keep the last value of each day
    dayMap.set(dayKey, point);
  }

  // Convert back to array and sort by timestamp
  return Array.from(dayMap.values()).sort((a, b) => a.timestamp - b.timestamp);
}

/**
 * Helper to convert cached user to PolymarketTrader format
 * Accepts both local PolymarketUser and db.ts PolymarketUser (null vs undefined)
 */
function cachedUserToTrader(cachedUser: PolymarketUser | import('@/lib/db').PolymarketUser): PolymarketTrader {
  // Convert string values from DB to numbers
  const pnl = Number(cachedUser.pnl) || 0;
  const volume = Number(cachedUser.volume) || 0;
  const positionsValue = Number(cachedUser.positions_value) || 0;
  const gain = Number(cachedUser.gain) || 0;
  const marketsTrades = Number(cachedUser.markets_traded) || 0;
  const wins = Number(cachedUser.wins) || 0;
  const losses = Number(cachedUser.losses) || 0;
  const winRate = Number(cachedUser.win_rate) || 0;

  // Parse market categories from JSON string
  let marketCategories: string[] | undefined;
  if (cachedUser.market_categories) {
    try {
      marketCategories = JSON.parse(cachedUser.market_categories);
    } catch {
      marketCategories = undefined;
    }
  }

  return {
    address: cachedUser.address,
    username: cachedUser.username ?? undefined,
    avatar: cachedUser.profile_image ?? "/avatar.jpg",
    bio: cachedUser.bio || (cachedUser.username ? `Polymarket trader @${cachedUser.username}` : "Polymarket trader"),
    totalVolume: volume,
    totalProfit: pnl > 0 ? pnl : 0,
    totalLoss: pnl < 0 ? Math.abs(pnl) : 0,
    totalPnL: pnl,
    currentBalance: positionsValue,
    positionsAboveEntry: gain ? Math.round(gain * 100) : 0,
    totalPositions: marketsTrades,
    activePositions: 0,
    wins: wins,
    losses: losses,
    winRate: winRate,
    joinedDate: cachedUser.created_at ? new Date(cachedUser.created_at).toISOString().split('T')[0] : undefined,
    marketCategories: marketCategories,
    // Stats from Polymarket scraping
    largestWin: (cachedUser as any).largest_win ? Number((cachedUser as any).largest_win) : undefined,
    profileViews: (cachedUser as any).profile_views ? Number((cachedUser as any).profile_views) : undefined,
    polymarketJoinedAt: (cachedUser as any).polymarket_joined_at ?? undefined,
  };
}

/**
 * Fetch fresh data from API and save to database
 */
async function fetchAndCacheTrader(
  address: string,
  upsertUser: (user: PolymarketUser) => Promise<void | PolymarketUser>
): Promise<PolymarketTrader | null> {
  const stats = await calculateTraderStatsFromHashDive(address);

  if (!stats) {

    return null;
  }

  const trader: PolymarketTrader = {
    address,
    username: stats.username,
    avatar: stats.avatar || "/avatar.jpg",
    bio: stats.bio || (stats.username ? `Polymarket trader @${stats.username}` : "Polymarket trader"),
    totalVolume: stats.totalVolume || 0,
    totalProfit: stats.totalProfit || 0,
    totalLoss: stats.totalLoss || 0,
    totalPnL: stats.totalPnL || 0,
    currentBalance: stats.currentBalance || 0,
    positionsAboveEntry: stats.positionsAboveEntry || 0,
    totalPositions: stats.totalPositions || 0,
    activePositions: stats.activePositions || 0,
    wins: stats.wins || 0,
    losses: stats.losses || 0,
    winRate: stats.winRate || 0,
    joinedDate: stats.joinedDate,
  };

  // Save to database
  try {
    const userToCache: PolymarketUser = {
      address: trader.address,
      username: trader.username,
      profile_image: trader.avatar,
      bio: trader.bio,
      pnl: trader.totalPnL,
      volume: trader.totalVolume,
      markets_traded: trader.totalPositions,
      positions_value: trader.currentBalance,
      gain: trader.positionsAboveEntry ? trader.positionsAboveEntry / 100 : undefined,
      num_trades: trader.wins + trader.losses,
      win_rate: trader.winRate,
      wins: trader.wins,
      losses: trader.losses,
    };

    await upsertUser(userToCache);
  } catch (cacheError) {
  }

  return trader;
}

/**
 * Fetch complete trader data by address using HashDive API (preferred) or Polymarket API (fallback)
 * Uses database cache to reduce API calls and improve performance
 *
 * Logic:
 * 1. If user is in database → return immediately from database
 * 2. If cache is stale (>24h) → refresh in background (non-blocking)
 * 3. If user is NOT in database → fetch from API and save to database
 */
export async function fetchRealTraderByAddress(
  address: string
): Promise<PolymarketTrader | null> {
  try {
    const backendProfile = await fetchBackendJson<{ trader?: PolymarketTrader }>(
      `/api/portfolio/traders/${encodeURIComponent(address)}`,
      30
    );
    if (backendProfile?.trader) {
      return backendProfile.trader;
    }

    // Dynamic import to avoid client-side errors
    const { getCachedUser, isCacheFresh, upsertUser } = await import('./db');

    // Check database first
    const cachedUser = await getCachedUser(address);

    if (cachedUser) {
      const trader = cachedUserToTrader(cachedUser);

      // If cache is stale, refresh in background (non-blocking)
      if (!isCacheFresh(cachedUser)) {
        // Fire and forget - don't await
        fetchAndCacheTrader(address, upsertUser).catch(() => {
        });
      }

      return trader;
    }

    const trader = await fetchAndCacheTrader(address, upsertUser);

    return trader;
  } catch (error) {
    return null;
  }
}
